import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithCredential,
  updatePhoneNumber,
  type User,
} from 'firebase/auth';
import { auth, refreshUser } from './firebase';
import { isPhoneVerificationSkipped, phonesMatch, toBrazilE164 } from '../utils/phone';
import { translateFirebaseErrorObject } from '../utils/firebaseErrors';

let recaptchaVerifier: RecaptchaVerifier | null = null;
let sendVerificationInFlight: Promise<string> | null = null;

function wrapFirebaseError(error: unknown): Error {
  return new Error(translateFirebaseErrorObject(error));
}

function getRecaptchaContainerId(): string {
  return 'firebase-phone-recaptcha';
}

function removeRecaptchaContainer(): void {
  document.getElementById(getRecaptchaContainerId())?.remove();
}

function createRecaptchaContainer(): HTMLElement {
  removeRecaptchaContainer();

  const element = document.createElement('div');
  element.id = getRecaptchaContainerId();
  element.className = 'fixed bottom-4 right-4 z-[300]';
  document.body.appendChild(element);
  return element;
}

export function clearPhoneRecaptcha(): void {
  try {
    recaptchaVerifier?.clear();
  } catch {
    /* widget may already be cleared */
  }
  recaptchaVerifier = null;
  removeRecaptchaContainer();
}

async function createRecaptchaVerifier(): Promise<RecaptchaVerifier> {
  clearPhoneRecaptcha();
  const container = createRecaptchaContainer();

  recaptchaVerifier = new RecaptchaVerifier(auth, container, {
    size: 'invisible',
    callback: () => undefined,
    'expired-callback': () => {
      clearPhoneRecaptcha();
    },
  });
  await recaptchaVerifier.render();

  return recaptchaVerifier;
}

async function sendPhoneVerificationCodeInternal(phone: string): Promise<string> {
  if (isPhoneVerificationSkipped()) {
    return 'dev-skip';
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }

  try {
    const provider = new PhoneAuthProvider(auth);
    const phoneE164 = toBrazilE164(phone);
    const verifier = await createRecaptchaVerifier();
    return await provider.verifyPhoneNumber(phoneE164, verifier);
  } catch (error) {
    clearPhoneRecaptcha();
    throw wrapFirebaseError(error);
  }
}

export async function sendPhoneVerificationCode(phone: string): Promise<string> {
  if (sendVerificationInFlight) {
    return sendVerificationInFlight;
  }

  sendVerificationInFlight = sendPhoneVerificationCodeInternal(phone).finally(() => {
    sendVerificationInFlight = null;
  });

  return sendVerificationInFlight;
}

async function linkPhoneCredential(
  user: User,
  verificationId: string,
  code: string,
): Promise<User> {
  const credential = PhoneAuthProvider.credential(verificationId, code);

  try {
    if (user.phoneNumber) {
      await updatePhoneNumber(user, credential);
    } else {
      await linkWithCredential(user, credential);
    }
  } catch (error: unknown) {
    const codeError = (error as { code?: string })?.code;
    if (
      codeError === 'auth/provider-already-linked' ||
      codeError === 'auth/credential-already-in-use'
    ) {
      await updatePhoneNumber(user, credential);
    } else {
      throw error;
    }
  }

  return refreshUser(user);
}

export async function confirmPhoneVerificationCode(
  phone: string,
  verificationId: string,
  code: string,
): Promise<User> {
  if (isPhoneVerificationSkipped()) {
    const user = auth.currentUser;
    if (!user) throw new Error('Usuário não autenticado.');
    return user;
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado.');
  }

  try {
    const updated = await linkPhoneCredential(user, verificationId, code);
    if (!updated.phoneNumber || !phonesMatch(updated.phoneNumber, phone)) {
      throw new Error('Telefone verificado não corresponde ao informado.');
    }
    clearPhoneRecaptcha();
    return updated;
  } catch (error) {
    throw wrapFirebaseError(error);
  }
}

export function isPhoneVerifiedOnFirebase(user: User | null, phone: string): boolean {
  if (isPhoneVerificationSkipped()) return !!phone.trim();
  if (!user?.phoneNumber || !phone.trim()) return false;
  return phonesMatch(user.phoneNumber, phone);
}
